import { DefaultQueryParams } from '@frontend/api-utils';
import { ProductExtendedClient } from '@frontend/product/api';
import { EquipmentProductExtended } from '@frontend/product/types';
import { useEffect, useState } from 'react';

import { LendingUnavailableProductsListCardProps } from './lending-unavailable-products-list-card.component';

const FETCH_SIZE = 100;
const LIST_LIMIT = 8;
/** Products with fewer units than this never count as "almost unavailable". */
const MIN_TOTAL_FOR_ALMOST = 2;
/** Available share at or below this counts as almost unavailable (when total is large enough). */
const ALMOST_UNAVAILABLE_MAX_RATIO = 0.25;

export type UnavailableProductSeverity = 'unavailable' | 'almost';

export interface UnavailableProductItem {
    product: EquipmentProductExtended;
    severity: UnavailableProductSeverity;
    total: number;
}

const getEquipmentTotal = (product: EquipmentProductExtended): number => product.available + product.in_use + product.unavailable;

const classifyProduct = (product: EquipmentProductExtended): UnavailableProductSeverity | null => {
    const total = getEquipmentTotal(product);
    if (total === 0) {
        return null;
    }
    if (product.available === 0) {
        return 'unavailable';
    }
    if (total >= MIN_TOTAL_FOR_ALMOST && product.available / total <= ALMOST_UNAVAILABLE_MAX_RATIO) {
        return 'almost';
    }
    return null;
};

const byAvailabilityPriority = (a: UnavailableProductItem, b: UnavailableProductItem): number => {
    if (a.severity !== b.severity) {
        return a.severity === 'unavailable' ? -1 : 1;
    }
    const aRatio = a.product.available / a.total;
    const bRatio = b.product.available / b.total;
    if (aRatio !== bRatio) {
        return aRatio - bRatio;
    }
    return a.total - b.total;
};

export const useLendingUnavailableProductsListCard = (props: LendingUnavailableProductsListCardProps) => {
    const [items, setItems] = useState<UnavailableProductItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!props.accountId) {
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        setIsLoading(true);

        ProductExtendedClient.fetchEquipmentsByProductExtended({
            [DefaultQueryParams.ACCOUNT_ID]: props.accountId,
            [DefaultQueryParams.SIZE]: String(FETCH_SIZE),
            [DefaultQueryParams.SORTFIELD]: 'available',
            [DefaultQueryParams.SORTDIRECTION]: 'asc'
        })
            .then((response) => {
                if (cancelled) {
                    return;
                }

                const ranked = (response.results ?? [])
                    .map((product) => {
                        const severity = classifyProduct(product);
                        if (!severity) {
                            return null;
                        }
                        return { product, severity, total: getEquipmentTotal(product) };
                    })
                    .filter((item): item is UnavailableProductItem => item !== null)
                    .sort(byAvailabilityPriority)
                    .slice(0, LIST_LIMIT);

                setItems(ranked);
            })
            .catch(() => {
                if (!cancelled) {
                    setItems([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [props.accountId]);

    return { items, isLoading };
};
